import { fetchAllCollections } from './shopifyClient';

// Transform Shopify collections into CategoryTreeNode format
export const transformShopifyCollectionsToCategories = async (): Promise<CategoryTreeNode[]> => {
  try {
    // Fetch all collections from Shopify
    const collections = await fetchAllCollections();
    
    // Create a map to store parent-child relationships
    const collectionsMap = new Map();
    const rootCollections: CategoryTreeNode[] = [];
    
    // First pass: create basic CategoryTreeNode objects for each collection
    collections.forEach(collection => {
      // Extract the numeric ID from the Shopify gid
      const idParts = collection.id.toString().split('/');
      const numericId = parseInt(idParts[idParts.length - 1], 10);
      
      // Create a CategoryTreeNode object
      const categoryNode: CategoryTreeNode = {
        crawler_id: numericId,
        id: numericId,
        name: collection.title,
        url_slug: collection.handle,
        parent_crawler_id: null, // We'll set this in the second pass
        children: [],
        total_count: 0 // This will be updated when we fetch products
      };
      
      // Store in the map for later reference
      collectionsMap.set(collection.id, categoryNode);
      
      // Check if this is a root collection or a child collection
      // For now, we'll treat all collections as root collections
      // In a future enhancement, we could parse collection titles or use metafields to determine hierarchy
      rootCollections.push(categoryNode);
    });
    
    // Second pass: identify parent-child relationships based on collection titles
    // This is a simple approach - in a real implementation, you might use metafields or a naming convention
    collections.forEach(collection => {
      const categoryNode = collectionsMap.get(collection.id);
      
      // Example: If collection title is "Parent - Child", set parent relationship
      const titleParts = collection.title.split(' - ');
      if (titleParts.length > 1) {
        // Find potential parent by name
        const potentialParentName = titleParts[0].trim();
        const potentialParent = Array.from(collectionsMap.values()).find(
          node => node.name === potentialParentName
        );
        
        if (potentialParent) {
          // Set parent-child relationship
          categoryNode.parent_crawler_id = potentialParent.crawler_id;
          categoryNode.name = titleParts[1].trim(); // Use only the child part for the name
          
          // Add to parent's children
          potentialParent.children.push(categoryNode);
          
          // Remove from root collections if it was there
          const rootIndex = rootCollections.findIndex(node => node.crawler_id === categoryNode.crawler_id);
          if (rootIndex !== -1) {
            rootCollections.splice(rootIndex, 1);
          }
        }
      }
    });
    
    // Return the root collections, which now have their children properly set
    return rootCollections;
  } catch (error) {
    console.error('Error transforming Shopify collections to categories:', error);
    return [];
  }
};
